import React, { useEffect, useState } from "react";
import Briciole from "@/components/Briciole";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Search, Archive, RotateCcw, FileText, Truck, ExternalLink, Clock, Building2, Trash2 } from "lucide-react";
import { DEFAULT_EMPLOYEE_PHOTO, photoUrl, fileUrl } from "@/lib/placeholders";
import { PERSONAL_DOC_TYPES } from "@/pages/Dipendenti";
import { useAuth } from "@/context/AuthContext";
import { eAmministratore } from "@/lib/ruoli";

const classOf = (c) =>
  c.doc_class ? c.doc_class : (PERSONAL_DOC_TYPES.includes(c.category) ? "personale" : "attestato");

export default function ArchivioDipendenti() {
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [openEmp, setOpenEmp] = useState(null);
  const { user } = useAuth();
  // Cancellare per sempre una persona e il suo fascicolo e' cosa da
  // amministratore: il server risponde solo a lui, e un pulsante che da'
  // «permessi insufficienti» e' peggio di un pulsante che non c'e'.
  const puoCancellare = eAmministratore(user);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/employees/archived");
      setList(data);
    } catch { toast.error("Errore caricamento archivio"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (emp) => {
    if (!window.confirm(`Riportare ${emp.full_name} tra i dipendenti attivi?`)) return;
    try {
      await api.post(`/employees/${emp.id}/restore`);
      toast.success("Dipendente ripristinato");
      load();
    } catch { toast.error("Errore"); }
  };

  const elimina = async (emp) => {
    const docs = emp.documents_count || 0;
    if (!window.confirm(
      `ELIMINARE DEFINITIVAMENTE ${emp.full_name}?\n\n` +
      `Vengono cancellati la scheda, la foto e ${docs} document${docs === 1 ? "o" : "i"} con i relativi file.\n` +
      `Lo storico delle consegne DPI resta, ma non sarà più collegato a un nominativo.\n\n` +
      `L'operazione non è reversibile.`
    )) return;
    if (!window.confirm(`Conferma definitiva: elimino ${emp.full_name} e i suoi documenti.`)) return;
    try {
      const { data } = await api.delete(`/employees/${emp.id}/permanent`);
      toast.success(`${emp.full_name} eliminato · ${data.deleted_documents} documenti rimossi`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const q = filter.trim().toLowerCase();
  const filtered = list.filter(e =>
    e.full_name.toLowerCase().includes(q) ||
    (e.badge_number || "").toLowerCase().includes(q) ||
    (e.role || "").toLowerCase().includes(q));

  return (
    <div className="space-y-6" data-testid="archivio-dipendenti-page">
      <Briciole voci={[
        { testo: "Dipendenti", a: "/dipendenti" },
        { testo: "Archivio ex dipendenti" },
      ]} />

      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Dipendenti</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">Archivio ex dipendenti</h1>
        <p className="text-sm text-slate-500 mt-1">
          {list.length} schede archiviate · documenti e consegne conservati integralmente
        </p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Cerca per nome, badge o mansione..." className="pl-9 h-11"
               value={filter} onChange={(e) => setFilter(e.target.value)} />
      </div>

      {loading && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}

      {!loading && filtered.length === 0 && (
        <Card className="border-dashed border-2">
          <CardContent className="p-10 text-center">
            <Archive className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <h3 className="font-display text-lg font-bold text-slate-700">Archivio vuoto</h3>
            <p className="text-sm text-slate-500 mt-1">
              Le schede dei dipendenti archiviati compariranno qui.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(emp => (
          <Card key={emp.id} className="border-slate-200 bg-slate-50/60">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <img src={photoUrl(emp.photo_url) || DEFAULT_EMPLOYEE_PHOTO} alt={emp.full_name}
                     className="w-14 h-14 rounded-full object-cover border-2 border-slate-200 grayscale flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-500 font-mono">{emp.badge_number}</div>
                  <div className="font-display font-bold text-lg leading-tight">{emp.full_name}</div>
                  <Badge variant="outline" className="mt-1">{emp.role}</Badge>
                </div>
              </div>

              <div className="text-xs text-slate-500 mt-3 space-y-0.5">
                {emp.archived_at && <div>Archiviato il {emp.archived_at.substring(0, 10)}</div>}
                {emp.archived_by && <div className="truncate">da {emp.archived_by}</div>}
              </div>

              <div className="flex gap-2 mt-3">
                <Badge variant="outline" className="text-[10px]">
                  <FileText className="w-3 h-3 mr-1" />{emp.documents_count ?? 0} documenti
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  <Truck className="w-3 h-3 mr-1" />{emp.deliveries_count ?? 0} consegne
                </Badge>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => setOpenEmp(emp)}
                        data-testid={`dossier-${emp.id}`}>
                  <FileText className="w-3 h-3 mr-1" /> Consulta fascicolo
                </Button>
                <Button variant="outline" size="sm" onClick={() => restore(emp)}
                        className="text-emerald-700 hover:bg-emerald-50">
                  <RotateCcw className="w-3 h-3 mr-1" /> Ripristina
                </Button>
                {puoCancellare && (
                  <Button variant="outline" size="sm" onClick={() => elimina(emp)}
                          className="text-red-600 hover:bg-red-50" title="Elimina definitivamente"
                          data-testid={`elimina-${emp.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {openEmp && <DossierDialog emp={openEmp} onClose={() => setOpenEmp(null)} />}
    </div>
  );
}

function DossierDialog({ emp, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/employees/${emp.id}/dossier`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Errore caricamento fascicolo"));
  }, [emp.id]);

  const docs = data?.documents || [];
  const personali = docs.filter(d => classOf(d) === "personale");
  const attestati = docs.filter(d => classOf(d) === "attestato");
  const deliveries = data?.deliveries || [];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            Fascicolo archiviato · {emp.full_name}
            <Badge variant="outline" className="font-normal">{emp.badge_number}</Badge>
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
          Sola consultazione. I documenti restano conservati per obblighi di tracciabilità;
          per modificarli occorre prima ripristinare il dipendente.
        </p>

        {!data && <p className="text-sm text-slate-500 py-8 text-center">Caricamento...</p>}

        {data && (
          <Tabs defaultValue="personali">
            <TabsList className="w-full justify-start overflow-x-auto">
              <TabsTrigger value="personali">Documenti personali ({personali.length})</TabsTrigger>
              <TabsTrigger value="attestati">Attestati ({attestati.length})</TabsTrigger>
              <TabsTrigger value="consegne">Consegne DPI ({deliveries.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="personali" className="space-y-2">
              {personali.length === 0
                ? <p className="text-sm text-slate-500 text-center py-8">Nessun documento</p>
                : personali.map(d => <ReadOnlyDoc key={d.id} d={d} />)}
            </TabsContent>

            <TabsContent value="attestati" className="space-y-2">
              {attestati.length === 0
                ? <p className="text-sm text-slate-500 text-center py-8">Nessun attestato</p>
                : attestati.map(d => <ReadOnlyDoc key={d.id} d={d} />)}
            </TabsContent>

            <TabsContent value="consegne">
              {deliveries.length === 0
                ? <p className="text-sm text-slate-500 text-center py-8">Nessuna consegna registrata</p>
                : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[520px]">
                      <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                        <tr>
                          <th className="text-left p-3">Data</th>
                          <th className="text-left p-3">ID</th>
                          <th className="text-left p-3">Articoli</th>
                          <th className="text-left p-3">Operatore</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deliveries.map(d => (
                          <tr key={d.id} className="border-t border-slate-100 align-top">
                            <td className="p-3 whitespace-nowrap">
                              {new Date(d.delivered_at).toLocaleDateString("it-IT")}
                            </td>
                            <td className="p-3"><Badge variant="outline">{d.id}</Badge></td>
                            <td className="p-3">
                              {(d.lines || []).map((l, i) => (
                                <div key={i} className="text-xs">
                                  {l.name} · taglia {l.size} · x{l.quantity}
                                </div>
                              ))}
                            </td>
                            <td className="p-3 text-xs text-slate-500">{d.operator_email}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReadOnlyDoc({ d }) {
  // Una riga per documento, come nel fascicolo.
  const data_it = (iso) => (iso ? iso.substring(0, 10).split("-").reverse().join("/") : "");
  return (
    <div className="px-2.5 py-1.5 rounded border border-slate-200 flex items-center gap-2">
      <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
      <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="font-semibold text-sm truncate max-w-full">{d.title || d.category}</span>
        {d.category !== d.title && <Badge variant="outline" className="text-[10px] py-0">{d.category}</Badge>}
        {d.tipologia && <Badge className="bg-slate-100 text-slate-700 border-0 text-[10px] py-0">{d.tipologia}</Badge>}
        {d.hours ? <Badge className="bg-slate-100 text-slate-700 border-0 text-[10px] py-0">
          <Clock className="w-3 h-3 mr-1" />{d.hours}h</Badge> : null}
        {d.issuer && <Badge className="bg-slate-100 text-slate-700 border-0 text-[10px] py-0">
          <Building2 className="w-3 h-3 mr-1" />{d.issuer}</Badge>}
        {d.archived && <Badge className="bg-slate-200 text-slate-600 border-0 text-[10px] py-0">archiviato</Badge>}
        <span className="text-xs text-slate-500 whitespace-nowrap">
          {d.issued_date && <>{data_it(d.issued_date)}</>}
          {d.expires_date && <> → {data_it(d.expires_date)}</>}
        </span>
      </div>
      {d.file_url && (
        <a href={fileUrl(d.file_url)} target="_blank" rel="noopener noreferrer" className="flex-shrink-0" title="Apri">
          <Button variant="outline" size="sm" className="h-7 w-7 p-0"><ExternalLink className="w-3.5 h-3.5" /></Button>
        </a>
      )}
    </div>
  );
}
